var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Form } from 'lincd-form/shapes/Form';
import { Thing } from 'lincd-schema/shapes/Thing';
import { xsd } from 'lincd-xsd/ontologies/xsd';
import { Boolean } from 'lincd-xsd/shapes/Boolean';
import { XSDDate } from 'lincd-xsd/shapes/XSDDate';
import { ShapeSet } from 'lincd/collections/ShapeSet';
import { Literal } from 'lincd/models';
import { literalProperty, objectProperty } from 'lincd/shapes/SHACL';
import { irlcg } from '../ontologies/irlcg.js';
import { linkedShape } from '../package.js';
import { Resource } from './Resource.js';
let ActionOption = class ActionOption extends Thing {
    /**
     * instances of this shape need to have exactly one value defined for the given property
     */
    get estimatedTimeMin() {
        return parseInt(this.getValue(irlcg.estimatedTimeMin));
    }
    set estimatedTimeMin(val) {
        this.overwrite(irlcg.estimatedTimeMin, new Literal(val.toString(), xsd.duration));
    }
    get estimatedTimeMax() {
        return parseInt(this.getValue(irlcg.estimatedTimeMax));
    }
    set estimatedTimeMax(val) {
        this.overwrite(irlcg.estimatedTimeMax, new Literal(val.toString(), xsd.duration));
    }
    get priority() {
        return Boolean.isTrue(this.getOne(irlcg.priority));
    }
    set priority(val) {
        this.overwrite(irlcg.priority, val ? Boolean.true() : Boolean.false());
    }
    get cycleDuration() {
        return parseInt(this.getValue(irlcg.cycleDuration));
    }
    set cycleDuration(val) {
        this.overwrite(irlcg.cycleDuration, new Literal(val.toString(), xsd.duration));
    }
    get cycleSetTime() {
        return this.getOneAs(irlcg.cycleSetTime, XSDDate);
    }
    set cycleSetTime(val) {
        this.overwrite(irlcg.cycleSetTime, val.node);
    }
    get timeDescription() {
        return this.getValue(irlcg.timeDescription);
    }
    set timeDescription(val) {
        this.overwrite(irlcg.timeDescription, new Literal(val, xsd.string));
    }
    get estimatedCost() {
        return parseInt(this.getValue(irlcg.estimatedCost));
    }
    set estimatedCost(val) {
        this.overwrite(irlcg.estimatedCost, new Literal(val.toString(), xsd.integer));
    }
    get estimatedCo2Reduction() {
        return parseInt(this.getValue(irlcg.estimatedCo2Reduction));
    }
    set estimatedCo2Reduction(val) {
        this.overwrite(irlcg.estimatedCo2Reduction, new Literal(val.toString(), xsd.integer));
    }
    get materials() {
        return Thing.getSetOf(this.getAll(irlcg.material));
    }
    get resources() {
        return Resource.getSetOf(this.getAll(irlcg.resource));
    }
    // @objectProperty({
    //   path: irlcg.steps,
    //   shape: StepSet,
    // })
    // get steps(): ShapeSet<StepSet> {
    //   return StepSet.getSetOf(this.getAll(irlcg.steps));
    // }
    get reportingForms() {
        return Form.getSetOf(this.getAll(irlcg.reportingForm));
    }
    get points() {
        return parseInt(this.getValue(irlcg.points));
    }
    set points(val) {
        this.overwrite(irlcg.points, new Literal(val.toString(), xsd.integer));
    }
    get isBonus() {
        return Boolean.isTrue(this.getOne(irlcg.isBonus));
    }
    set isBonus(val) {
        this.overwrite(irlcg.isBonus, val ? Boolean.true() : Boolean.false());
    }
    get isCustom() {
        return Boolean.isTrue(this.getOne(irlcg.isCustom));
    }
    set isCustom(val) {
        this.overwrite(irlcg.isCustom, val ? Boolean.true() : Boolean.false());
    }
};
/**
 * indicates that instances of this shape need to have this rdf.type
 */
ActionOption.targetClass = irlcg.ActionOption;
__decorate([
    literalProperty({
        path: irlcg.estimatedTimeMin,
        datatype: xsd.duration,
        maxCount: 1,
        description: 'Estimated time to complete an action in minutes',
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "estimatedTimeMin", null);
__decorate([
    literalProperty({
        path: irlcg.estimatedTimeMax,
        datatype: xsd.duration,
        maxCount: 1,
        description: 'Estimated maximum time to complete an action in minutes',
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "estimatedTimeMax", null);
__decorate([
    literalProperty({
        path: irlcg.priority,
        datatype: Boolean.targetClass,
        maxCount: 1,
        description: 'High priority action flag',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], ActionOption.prototype, "priority", null);
__decorate([
    literalProperty({
        path: irlcg.cycleDuration,
        datatype: xsd.duration,
        maxCount: 1,
        description: 'Recurring cycle length in days',
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "cycleDuration", null);
__decorate([
    literalProperty({
        path: irlcg.cycleSetTime,
        description: 'When recurring cycle was established',
    }),
    __metadata("design:type", XSDDate),
    __metadata("design:paramtypes", [XSDDate])
], ActionOption.prototype, "cycleSetTime", null);
__decorate([
    literalProperty({
        path: irlcg.timeDescription,
        datatype: xsd.string,
        description: 'Human-readable time requirements explanation',
    }),
    __metadata("design:type", String),
    __metadata("design:paramtypes", [String])
], ActionOption.prototype, "timeDescription", null);
__decorate([
    literalProperty({
        path: irlcg.estimatedCost,
        datatype: xsd.integer,
        description: 'Expected monetary cost in dollars',
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "estimatedCost", null);
__decorate([
    literalProperty({
        path: irlcg.estimatedCo2Reduction,
        datatype: xsd.integer,
        description: 'CO2 reduction in kg',
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "estimatedCo2Reduction", null);
__decorate([
    objectProperty({
        path: irlcg.material,
        shape: Thing,
        description: 'Physical items required to compleate the action.',
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], ActionOption.prototype, "materials", null);
__decorate([
    objectProperty({
        path: irlcg.resource,
        shape: Resource,
        description: 'Information resources, guides, local businesses, purchaese links etc.',
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], ActionOption.prototype, "resources", null);
__decorate([
    objectProperty({
        path: irlcg.reportingForm,
        shape: Form,
        description: 'Forms for tracking completion',
    }),
    __metadata("design:type", ShapeSet),
    __metadata("design:paramtypes", [])
], ActionOption.prototype, "reportingForms", null);
__decorate([
    literalProperty({
        path: irlcg.points,
        datatype: xsd.integer,
        description: 'Legacy: point value (system now counts actions instead)',
        maxCount: 1,
    }),
    __metadata("design:type", Number),
    __metadata("design:paramtypes", [Number])
], ActionOption.prototype, "points", null);
__decorate([
    literalProperty({
        path: irlcg.isBonus,
        datatype: Boolean.targetClass,
        maxCount: 1,
        description: 'Flag for an Optional extra action beyond required actions for topic.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], ActionOption.prototype, "isBonus", null);
__decorate([
    literalProperty({
        path: irlcg.isCustom,
        datatype: Boolean.targetClass,
        maxCount: 1,
        description: 'User-created vs system template for an action under a topic.',
    }),
    __metadata("design:type", Boolean),
    __metadata("design:paramtypes", [Boolean])
], ActionOption.prototype, "isCustom", null);
ActionOption = __decorate([
    linkedShape({
        description: 'A template defining specific action options players can choose from. Represents action details with time estimates, costs, materials, resources, and action values. (template, option, choice)',
    })
], ActionOption);
export { ActionOption };
//# sourceMappingURL=ActionOption.js.map